<script module lang="ts">
  type Variant = VariantProps<ButtonGroupVariants>["variant"]

  type SingleButtonRootProps = {
    type?: "single"
    value?: any
  }

  type MultipleButtonRootProps = {
    type: "multiple"
    value?: string[] | number[] | null
  }

  export type ButtonGroupRootProps = BaseProps<"div"> &
    (SingleButtonRootProps | MultipleButtonRootProps) & {
      name?: string
      disabled?: boolean
      deselect?: boolean
      loop?: boolean
      rovingFocus?: boolean
      variant?: Variant
      dir?: Direction
      orientation?: Orientation
      use?: any[]
    }

  export type ButtonGroupContext = {
    readonly type: "single" | "multiple"
    readonly orientation: Orientation
    readonly dir: Direction
    readonly loop: boolean
    readonly rovingFocus: boolean
    readonly disabled: ButtonGroupRootProps["disabled"]
    readonly deselect: boolean
    readonly name: string | undefined
    readonly variant: Variant
    value: ButtonGroupRootProps["value"]
  }

  const defaults = {
    type: "single",
    name: undefined,
    disabled: false,
    deselect: false,
    loop: true,
    rovingFocus: true,
    variant: "default",
    dir: Directions.LTR,
    orientation: Orientations.HORIZONTAL,
    value: null,
  } satisfies Defaults<ButtonGroupContext>

  const { setContext, getContext } =
    reactiveContext<ButtonGroupContext>(defaults)
  export const getButtonGroupRootContext = getContext
  const itemCollection = collectionContext()
  export const getButtonGroupItemCollection = itemCollection.getContext
</script>

<script lang="ts">
  import {
    Directions,
    Orientations,
    type Direction,
    type Orientation,
  } from "#lib/internal/constants"
  import {
    collectionContext,
    getUiclContext,
    reactiveContext,
    useActions,
    type Defaults,
  } from "#lib/internal/helpers"
  import { enableGroupKeyboardNavigation } from "#lib/internal/helpers/group"
  import type { BaseProps } from "#lib/internal/types"
  import type { VariantProps } from "class-variance-authority"
  import { untrack, type Snippet } from "svelte"
  import { buttonGroupRootVariants, type ButtonGroupVariants } from "./variants"

  type Props = ButtonGroupRootProps & { children?: Snippet }

  let {
    class: className = "",
    type = defaults.type,
    name = defaults.name,
    disabled = defaults.disabled,
    deselect = type === "multiple",
    loop = defaults.loop,
    rovingFocus = defaults.rovingFocus,
    variant = defaults.variant,
    dir = defaults.dir,
    orientation = defaults.orientation,
    role = "group",
    value = $bindable(),
    use = [],
    children,
    ...restProps
  }: Props = $props()

  const ctx = setContext({
    value: (v) => {
      if (v !== value) value = v
    },
  })

  $effect.pre(() => {
    const state = {
      variant,
      value,
      name,
      type,
      orientation,
      dir,
      disabled,
      deselect,
      loop,
      rovingFocus,
    }
    untrack(() => ctx.set(state))
  })

  const itemStore = itemCollection.setContext()
  const listeners = new Map()
  $effect(() => {
    enableGroupKeyboardNavigation(itemStore, listeners, orientation, dir)
  })

  const v2 = !!getUiclContext()?.v2
</script>

<div
  {role}
  {dir}
  {...restProps}
  data-disabled={disabled}
  data-orientation={orientation}
  class={buttonGroupRootVariants({ variant, className, v2 })}
  use:useActions={use}
>
  {@render children?.()}
</div>
